/**
 * Prompt text, with its placeholders as objects rather than punctuation.
 *
 * An author writing 「{{clinic_name}}の{{city}}院では」 is reading two pieces of
 * machine syntax in the middle of a Japanese sentence, in a language the rest of
 * the screen never uses. They cannot tell at a glance which names are real -
 * `{{clinic_naem}}` looks exactly as valid - and the only way to find out is to
 * save and be told by the lint.
 *
 * Here a placeholder is a chip: 「クリニック名」 on brand, one object to the
 * caret, deleted whole by one press of Backspace. What is stored is unchanged -
 * `{{clinic_name}}` in, `{{clinic_name}}` out - so the server, the lint and
 * every prompt already written see exactly what they saw before.
 *
 * Why contenteditable rather than a textarea with an overlay: a chip has to sit
 * ON the text baseline and wrap with it. An overlay has to mirror the textarea's
 * wrapping exactly, forever, across fonts and zoom levels, and drifts the moment
 * either changes.
 *
 * The two rules that make contenteditable survivable, both of which exist
 * because this application is written in Japanese:
 *
 *   The DOM is never rebuilt while the caret is in it. Rebuilding on every
 *   keystroke is what makes these editors lose the caret and drop characters.
 *   The value flows out on input; it only flows IN when the text changed for a
 *   reason other than typing here.
 *
 *   Nothing is read or written during IME composition. Japanese input is a
 *   sequence of intermediate states - かな being converted to kanji - and a
 *   serialize in the middle of that reads text the author has not committed to
 *   and can cancel. `compositionstart`/`compositionend` bracket it.
 */
import { useCallback, useEffect, useRef } from 'react';
import { variableLabel } from '../variable-labels.ts';

const TOKEN = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;

/** The text as the DOM currently holds it, back in `{{name}}` form. */
function serialize(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.nodeValue ?? '';
        continue;
      }
      if (child instanceof HTMLElement) {
        const token = child.dataset.token;
        if (token) {
          out += `{{${token}}}`;
          continue;
        }
        // A browser ends a line with <br> in some cases and a block element in
        // others; both mean the same thing to the text being written.
        if (child.tagName === 'BR') {
          out += '\n';
          continue;
        }
        if (getComputedStyle(child).display !== 'inline' && out !== '' && !out.endsWith('\n')) {
          out += '\n';
        }
        walk(child);
      }
    }
  };
  walk(root);
  return out;
}

/** A chip element for one placeholder. */
function chip(name: string, known: boolean): HTMLElement {
  const span = document.createElement('span');
  span.className = `vchip${known ? '' : ' vchip--unknown'}`;
  span.dataset.token = name;
  span.contentEditable = 'false';
  span.textContent = variableLabel(name);
  // The real name, for anybody who needs to know what it will resolve to.
  span.title = `{{${name}}}`;
  return span;
}

/** Fill the element from `{{name}}` text. Only ever called when not focused. */
function render(root: HTMLElement, value: string, allowed: readonly string[]): void {
  root.replaceChildren();
  let at = 0;
  for (const match of value.matchAll(TOKEN)) {
    const start = match.index;
    if (start > at) root.append(document.createTextNode(value.slice(at, start)));
    root.append(chip(match[1]!.toLowerCase(), allowed.includes(match[1]!.toLowerCase())));
    at = start + match[0].length;
  }
  if (at < value.length) root.append(document.createTextNode(value.slice(at)));
  // An empty editable with no text node puts the caret nowhere.
  if (root.childNodes.length === 0) root.append(document.createTextNode(''));
}

export function TokenText({
  id,
  value,
  onChange,
  /** Names that resolve. Anything else is shown, and marked as not resolving. */
  variables,
  placeholder,
  onFocus,
  minRows = 4,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  variables: readonly string[];
  placeholder?: string;
  onFocus?: () => void;
  minRows?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const composing = useRef(false);
  /**
   * What this editor last emitted, so an echo of it is not a reason to rebuild.
   *
   * Deliberately NOT seeded with `value`: seeding it meant the first effect saw
   * `value === emitted` and returned, so a prompt opened for editing rendered
   * an empty box. The sentinel cannot equal any string, so the first pass
   * always draws.
   */
  const emitted = useRef<string | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    // Typing here produced this value; rebuilding the DOM under the caret is
    // how a chip editor loses the caret and drops characters.
    if (value === emitted.current) return;
    if (document.activeElement === root) return;
    render(root, value, variables);
  }, [value, variables]);

  const emit = useCallback(() => {
    const root = ref.current;
    if (!root || composing.current) return;
    const next = serialize(root);
    emitted.current = next;
    onChange(next);
  }, [onChange]);

  /**
   * Put a placeholder where the caret is.
   *
   * Exposed through the element so the toolbar above can reach it without this
   * component owning the list of variables, which belongs to the prompt.
   */
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const insert = (event: Event) => {
      const name = (event as CustomEvent<string>).detail;
      const selection = window.getSelection();
      const range =
        selection && selection.rangeCount > 0 && root.contains(selection.anchorNode)
          ? selection.getRangeAt(0)
          : null;

      const node = chip(name, variables.includes(name));
      if (range) {
        range.deleteContents();
        range.insertNode(node);
        // A space after it, so the next character typed is not swallowed into
        // the chip's own text node.
        const after = document.createTextNode(' ');
        node.after(after);
        range.setStartAfter(after);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
      } else {
        root.append(node, document.createTextNode(' '));
      }
      root.focus();
      emit();
    };
    root.addEventListener('insert-variable', insert);
    return () => root.removeEventListener('insert-variable', insert);
  }, [variables, emit]);

  return (
    <div
      id={id}
      ref={ref}
      className="tokentext"
      role="textbox"
      aria-multiline="true"
      tabIndex={0}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      style={{ minHeight: `${minRows * 1.7}em` }}
      onInput={emit}
      onFocus={onFocus}
      onBlur={emit}
      onCompositionStart={() => {
        composing.current = true;
      }}
      onCompositionEnd={() => {
        composing.current = false;
        emit();
      }}
      onPaste={(event) => {
        /*
         * Plain text only. A paste from a document carries fonts, colours and
         * spans that would be serialized as text but rendered as somebody
         * else's styling inside a brand-coloured editor.
         */
        event.preventDefault();
        const text = event.clipboardData.getData('text/plain');
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(text));
        range.collapse(false);
        emit();
      }}
    />
  );
}

/** Ask a `TokenText` to insert a placeholder at its caret. */
export function insertVariableInto(element: HTMLElement | null, name: string): void {
  element?.dispatchEvent(new CustomEvent('insert-variable', { detail: name }));
}
